import esTranslations from './locales/es.json'
import enTranslations from './locales/en.json'
import frTranslations from './locales/fr.json'

export type Language = "en" | "es" | "fr"

export interface Translations {
  [key: string]: any
}

// Language order: English, Spanish, French
// English as default (international audience)
export const SUPPORTED_LANGUAGES: Language[] = ["en", "es", "fr"]
export const DEFAULT_LANGUAGE: Language = "es"

const translations: Record<Language, Translations> = {
  en: enTranslations,
  es: esTranslations,
  fr: frTranslations
}

export const getTranslations = (language: Language): Translations => {
  return translations[language] || translations[DEFAULT_LANGUAGE]
}

export const getSupportedLanguages = (): Language[] => {
  return SUPPORTED_LANGUAGES
}

export const isLanguageSupported = (language: string): language is Language => {
  return SUPPORTED_LANGUAGES.includes(language as Language)
}

// Language detection utility
export const detectBrowserLanguage = (): Language => {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE

  // Get browser languages in preference order
  const browserLanguages = navigator.languages || [navigator.language]

  for (const browserLang of browserLanguages) {
    // Extract language code (e.g., 'en-US' -> 'en')
    const langCode = browserLang.split('-')[0].toLowerCase()

    // Check if we support this language
    if (isLanguageSupported(langCode)) {
      return langCode as Language
    }
  }

  return DEFAULT_LANGUAGE
}

// Get language from URL path
export const getLanguageFromPath = (pathname: string): Language | null => {
  const segments = pathname.split('/').filter(Boolean)
  const langSegment = segments[0]

  if (langSegment && isLanguageSupported(langSegment)) {
    return langSegment as Language
  }

  return null
}

// Remove language from path
export const removeLanguageFromPath = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean)
  const langSegment = segments[0]

  if (langSegment && isLanguageSupported(langSegment)) {
    return '/' + segments.slice(1).join('/')
  }

  return pathname
}

// Add language to path
export const addLanguageToPath = (pathname: string, language: Language): string => {
  const cleanPath = removeLanguageFromPath(pathname)
  return `/${language}${cleanPath === '/' ? '' : cleanPath}`
}

export const translate = (
  translations: Translations,
  key: string,
  params?: Record<string, any>
): string => {
  const keys = key.split(".")
  let value: any = translations

  for (const k of keys) {
    value = value?.[k]
  }

  if (typeof value !== "string") {
    return key // Fallback to key if translation not found
  }

  // Process i18next pluralization syntax: {count, plural, =1 {...} other {...}}
  if (params && params.count !== undefined) {
    // Find the start of a plural expression
    const pluralStartRegex = /\{(\w+),\s*plural\s*,\s*/
    const pluralStartMatch = value.match(pluralStartRegex)
    
    if (pluralStartMatch) {
      const paramName = pluralStartMatch[1]
      const count = params[paramName] || params.count
      const startPos = pluralStartMatch.index! + pluralStartMatch[0].length
      
      // Find the matching closing brace by counting braces
      let braceCount = 1
      let endPos = startPos
      
      while (endPos < value.length && braceCount > 0) {
        if (value[endPos] === '{') braceCount++
        if (value[endPos] === '}') braceCount--
        if (braceCount > 0) endPos++
      }
      
      if (braceCount === 0) {
        const pluralRules = value.substring(startPos, endPos)
        
        // Parse plural rules
        const rules: Record<string, string> = {}
        
        // Match =1 {text} pattern
        const exactMatch = pluralRules.match(/=1\s*\{/)
        if (exactMatch) {
          const ruleStart = exactMatch.index! + exactMatch[0].length - 1
          let ruleBraceCount = 1
          let ruleEnd = ruleStart + 1
          
          while (ruleEnd < pluralRules.length && ruleBraceCount > 0) {
            if (pluralRules[ruleEnd] === '{') ruleBraceCount++
            if (pluralRules[ruleEnd] === '}') ruleBraceCount--
            if (ruleBraceCount > 0) ruleEnd++
          }
          
          if (ruleBraceCount === 0) {
            rules['=1'] = pluralRules.substring(ruleStart + 1, ruleEnd).trim()
          }
        }
        
        // Match other {text} pattern
        const otherMatch = pluralRules.match(/other\s*\{/)
        if (otherMatch) {
          const ruleStart = otherMatch.index! + otherMatch[0].length - 1
          let ruleBraceCount = 1
          let ruleEnd = ruleStart + 1
          
          while (ruleEnd < pluralRules.length && ruleBraceCount > 0) {
            if (pluralRules[ruleEnd] === '{') ruleBraceCount++
            if (pluralRules[ruleEnd] === '}') ruleBraceCount--
            if (ruleBraceCount > 0) ruleEnd++
          }
          
          if (ruleBraceCount === 0) {
            rules['other'] = pluralRules.substring(ruleStart + 1, ruleEnd).trim()
          }
        }
        
        // Determine which rule to use
        let selectedRule: string | undefined
        
        if (count === 1 && rules['=1']) {
          selectedRule = rules['=1']
        } else if (rules['other']) {
          selectedRule = rules['other']
        }
        
        if (selectedRule) {
          // Replace # with the count value
          selectedRule = selectedRule.replace(/#/g, count.toString())
          // Replace the entire plural expression with the selected rule
          const fullPluralExpr = value.substring(pluralStartMatch.index!, endPos + 1)
          value = value.replace(fullPluralExpr, selectedRule)
        }
      }
    }
  }

  // Simple parameter replacement - support both {{param}} and {param} formats
  if (params) {
    return value
      .replace(/\{\{(\w+)\}\}/g, (match: string, paramKey: string) => {
        return params[paramKey]?.toString() || match
      })
      .replace(/\{(\w+)\}/g, (match: string, paramKey: string) => {
        // Skip if this is part of a plural rule we already processed
        if (match.includes('plural')) return match
        return params[paramKey]?.toString() || match
      })
  }

  return value
}

export { translations } 